import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Title } from "../../themes/Default/components/Title";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button } from "../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../themes/Default/components/List";
import { Header as ListHeader } from "../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../themes/Default/components/List/components/Body";
import { Grid as ListGrid } from "../../themes/Default/components/List/components/Grid";
import { Card } from "../../themes/Default/components/List/components/Grid/components/Card";
import { Pagination } from "../../themes/Default/components/List/components/Footer/components/Pagination";
import { TableHead } from "../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Title as ListTitle } from "../../themes/Default/components/List/components/Header/components/Title";
import { Menu as ListMenu } from "../../themes/Default/components/List/components/Header/components/Menu";
import { Filter as ListFilter } from "../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Checkbox } from "../../themes/Default/components/Form/components/Checkbox";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { studentsManagement } from "../../hooks/api/StudentsManagement";
import { getNumberOfUsedFilters } from "../../services/utils/Filter";
import { useTranslation } from "react-i18next";
import { Toaster } from "../../themes/Default/components/Toaster";
import approvedIcon from "../../assets/icons/approved.svg";
import errorIcon from "../../assets/icons/error.svg";
import Search from "../../themes/Default/components/List/components/Header/components/Menu/components/Search/Search";
import { roles } from "../../services/utils/Roles";
import { useUserToken } from "../../hooks/core/UserToken";
import { isGridView } from "../../services/core/User/IsGridView";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { prepareSelect } from "../../services/core/Select";
import { usePersistentFilters } from "../../hooks/core/PersistentFilters";
import DatePicker from "../../themes/Default/components/Form/components/DatePicker/DatePicker";

const Students = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 50;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
        search: ""
    };
    const [filters, setFilters, resetFilters] = usePersistentFilters("studentsFilters", defaultFilters);
    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();
    const [totalRecords, setTotalRecords] = useState(0);
    const [students, setStudents] = useState();
    const { useStudentsListMutation, useDeleteStudentsMutation, useViewAvatarMutation, useExportStudentMutation } = studentsManagement;
    const [studentList, { isLoading }] = useStudentsListMutation();
    const [deleteStudent] = useDeleteStudentsMutation();
    const [isGridActive, setIsGridActive] = useState(isGridView(decodedToken, 'students'));
    const [viewAvatar] = useViewAvatarMutation();
    const [avatars, setAvatars] = useState({});
    const [exportStudents] = useExportStudentMutation();

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const gotoNew = () => {
        navigate("/students/add");
    };

    const gotoEdit = (studentId) => {
        navigate("/students/edit/" + studentId);
    };

    const handleGridView = () => {
        setIsGridActive(!isGridActive);
    };

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNumber: values.phoneNumber,
            emailAddress: values.emailAddress,
            hasContract: values.hasContract,
            signeContract: values.signeContract,
            hasGroup: values.hasGroup,
            contractStartDate: values?.contractStartDate,
            contractEndDate: values?.contractEndDate
        });
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedStudents((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDeleteOne = (id, firstName, lastName) => {
        toast.warn(
            ({ closeToast }) => (
                <div className="notification">
                    <div className="notification-message">
                        <p className="notification-message__title">
                            {firstName} {lastName}
                        </p>
                        <p className="notification-message__description">{t("areYouSure")}</p>
                    </div>
                    <div className="notification-options">
                        <div
                            className="notification-options__no"
                            onClick={() => {
                                closeToast();
                            }}
                        >
                            <img src={errorIcon}></img>
                            <span className="span-no">{t("no")}</span>
                        </div>
                        <div
                            className="notification-options__yes"
                            onClick={() => {
                                closeToast();
                                const studentId = id;
                                deleteStudent(studentId).then((result) => {
                                    if (result?.error) {
                                        toast.error(result.error.data.error.responseMessage);
                                    } else {
                                        studentList(filters).then((result) => {
                                            const students = result?.data?.records;
                                            setStudents(students);
                                            setTotalRecords(result?.data?.totalRecords);
                                        });
                                        setDisableDelete(true);
                                        toast.info(result?.data?.info?.responseMessage);
                                        onSelectionChange(studentId);
                                    }
                                });
                            }}
                        >
                            <img src={approvedIcon}></img>
                            <span className="span-yes">{t("yes")}</span>
                        </div>
                    </div>
                </div>
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedStudents?.length; i++) {
                            const studentId = selectedStudents[i];
                            deleteStudent(studentId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    studentList(filters).then((result) => {
                                        const students = result?.data?.records;
                                        setStudents(students);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(studentId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onExport = async () => {
        const blob = await exportStudents().unwrap();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date().toISOString().split('T')[0];
        link.download = `Students_${today}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const getInitials = (firstName, lastName) => {
        let initials = firstName.charAt(0) + lastName.charAt(0);

        return initials;
    };

    const avatarUrl = (studentId) => {
        viewAvatar(studentId).then((result) => {
            if (result?.data?.avatar) {
                setAvatars((prevAvatars) => {
                    const updatedAvatars = { ...prevAvatars, [studentId]: result?.data?.avatar };
                    return updatedAvatars;
                });
            }
        });
    };

    const [searchValue, setSearchValue] = useState("");

    const handleSearchChange = (value) => {
        setSearchValue(value);
    };

    const handleSearchSubmit = (value) => {
        setFilters((prev) => ({
            ...prev,
            search: value,
        }));
    };

    useEffect(() => {
        if (selectedStudents.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedStudents]);

    useEffect(() => {
        studentList(filters).then((result) => {
            const students = result?.data?.records;
            setStudents(students);

            setTotalRecords(result?.data?.totalRecords);

            if (students && Array.isArray(students)) {
                students.forEach((student) => {
                    avatarUrl(student.id);
                });
            }
        });
        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));
    }, [filters, studentList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <Button title={t("students.addStudent")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) || decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)) && (
                    <>
                        <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
                    </>
                )} 
                <Button title={t("export")} icon="fa-regular fa-cloud-arrow-down" onClick={onExport}></Button>
            </TopNavigation>
            <Main>
                <Title>{t("students.title")}</Title>
                <Breadcrumps>
                    <Link title={t("students.title")} icon="fa-user-graduate" />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List isGridActive={isGridActive}>
                            <ListHeader>
                                <ListTitle title={`${t("students.title")} (${totalRecords})`} subtitle="Equilibrio Kids and Teens" />
                                <ListMenu>
                                    <Search
                                        value={searchValue}
                                        onChange={handleSearchChange}
                                        onSearch={handleSearchSubmit}
                                    />
                                    <ListMenuButton
                                        title={isGridActive ? "List view" : "Card view"}
                                        icon={isGridActive ? "fa-list-ul" : "fa-grid-2"}
                                        onClick={handleGridView}
                                    />
                                    <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                                </ListMenu>
                                <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={resetFilters}>
                                    <Formik
                                        innerRef={formRef}
                                        initialValues={{
                                            firstName: filters?.firstName,
                                            lastName: filters?.lastName,
                                            phoneNumber: filters?.phoneNumber,
                                            emailAddress: filters?.emailAddress,
                                            hasContract: filters?.hasContract,
                                            signeContract: filters?.signeContract,
                                            hasGroup: filters?.hasGroup,
                                            contractStartDate: filters?.contractStartDate,
                                            contractEndDate: filters?.contractEndDate
                                        }}
                                        onSubmit={onFiltered}
                                    >
                                        {({ values, handleChange, handleSubmit, resetForm }) => (
                                            <Form>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="firstName"
                                                            label={t("students.firstName")}
                                                            value={values.firstName}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input name="lastName" label={t("students.lastName")} value={values.lastName} onChange={handleChange} />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="phoneNumber"
                                                            label={t("students.phoneNumber")}
                                                            value={values.phoneNumber}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="emailAddress"
                                                            label={t("students.emailAddress")}
                                                            value={values.emailAddress}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Select
                                                            name="hasContract"
                                                            label={t("Postoji ugovor")}
                                                            options={prepareSelect([
                                                                { id: "true", name: t("students.canBePhotographedYes") },
                                                                { id: "false", name: t("students.canBePhotographedNo") },
                                                            ])}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                            }}
                                                            selectedValue={values.hasContract}
                                                            searchVisible={false}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Select
                                                            name="signeContract"
                                                            label={t("Potpisan ugovor")}
                                                            options={prepareSelect([
                                                                { id: "true", name: t("students.canBePhotographedYes") },
                                                                { id: "false", name: t("students.canBePhotographedNo") },
                                                            ])}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                            }}
                                                            selectedValue={values.signeContract}
                                                            searchVisible={false}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Select
                                                            name="hasGroup"
                                                            label={t("Postoji grupa")}
                                                            options={prepareSelect([
                                                                { id: "true", name: t("students.canBePhotographedYes") },
                                                                { id: "false", name: t("students.canBePhotographedNo") },
                                                            ])}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                            }}
                                                            selectedValue={values.hasGroup}
                                                            searchVisible={false}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <DatePicker
                                                            name="contractStartDate"
                                                            label={t("Ugovori od")}
                                                            value={values.contractStartDate ?? ""}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>  
                                                <Row>
                                                    <Field>
                                                        <DatePicker
                                                            name="contractEndDate"
                                                            label={t("Ugovori do")}
                                                            value={values.contractEndDate ?? ""}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>  
                                            </Form>
                                        )}
                                    </Formik>
                                </ListFilter>
                            </ListHeader>
                            {isGridActive ? (
                                <ListGrid>
                                    {students?.length > 0 &&
                                        students?.map((student) => {
                                            return (
                                                <Card
                                                    key={student.id}
                                                    firstName={student.firstName}
                                                    lastName={student.lastName}
                                                    avatar={avatars[student.id]}
                                                    phoneNumber={student.guardian1PhoneNumber}
                                                    email={student.guardian1Email}
                                                    onEditClick={() => gotoEdit(student.id)}
                                                    onDeleteClick={() =>
                                                        onDeleteOne(
                                                            student.id,
                                                            student.firstName,
                                                            student.lastName,
                                                            avatars[student.id],
                                                            getInitials(student.firstName, student.lastName),
                                                        )
                                                    }
                                                    initials={getInitials(student.firstName, student.lastName)}
                                                />
                                            );
                                        })}
                                </ListGrid>
                            ) : (
                                <ListBody>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeadCell></TableHeadCell>
                                            <TableHeadCell></TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("students.firstName")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("students.lastName")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("students.dateOfBirth")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("students.guardian")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("students.phoneNumber")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("groups.title")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("students.signedContracts")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell></TableHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {students?.length > 0 &&
                                            students?.map((student, index) => {
                                                return (
                                                    <TableRow key={student.id}>
                                                        <TableDataCell>
                                                            <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(student.id)}></Button>
                                                        </TableDataCell>
                                                        <TableDataCell>
                                                            <Checkbox name="isBlockClass" value={student.id} onChange={() => onSelectionChange(student.id)} />
                                                        </TableDataCell>
                                                        <TableDataCell>{student.firstName}</TableDataCell>
                                                        <TableDataCell>{student.lastName}</TableDataCell>
                                                        <TableDataCell>{student.dateOfBirth}</TableDataCell>
                                                        <TableDataCell>{student.guardian1FirstName + ' ' + student.guardian1LastName}</TableDataCell>
                                                        <TableDataCell>{student.guardian1PhoneNumber}</TableDataCell>
                                                        <TableDataCell title={student.groups.filter(g => g.studentStatus === false).map(g => g.name).join('\n')}>
                                                            {student.groupsCount > 0 ? student.groupsCount : ''}
                                                        </TableDataCell>
                                                        <TableDataCell>
                                                            <span style={{ color: student.signeContract === student.totalContract ? '#27ae60' : '#ff0000' }}>
                                                                {(student.totalContract > 0) ? student.signeContract + '/' + student.totalContract : ''}
                                                            </span>
                                                        </TableDataCell>
                                                        <TableDataCell>
                                                        </TableDataCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </ListBody>
                            )}
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Students;
